import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Trash2 } from "lucide-react";

/**
 * Create/edit a Site Service provider. Fields:
 *   name, kind, interval, first_visit_date, contact_name, phone, email,
 *   website, cost_per_visit, notes.
 * Managers only. Delete is a soft-delete (server-side is_deleted flag).
 */
const SiteServiceFormDialog = ({ open, provider, meta, onClose, onSaved, onMetaChange }) => {
  const isEdit = !!provider?.id;
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(
      isEdit
        ? { ...provider }
        : {
            name: "",
            kind: meta?.kinds?.[0] || "Other",
            interval: "Quarterly",
            first_visit_date: "",
            contact_name: "",
            phone: "",
            email: "",
            website: "",
            cost_per_visit: "",
            notes: "",
            auto_log: false,
          },
    );
  }, [open, provider, meta, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addNewKind = async () => {
    // eslint-disable-next-line no-alert
    const raw = window.prompt(
      "New provider kind (e.g. Solar Cleaning, Coffee Machine Servicing):",
      "",
    );
    if (raw == null) return; // cancelled
    const name = raw.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/site-services/kinds", { name });
      toast.success(
        data.created ? `Added "${data.name}"` : `Already had "${data.name}"`,
      );
      // Ask the parent to reload meta so the dropdown includes the new
      // kind next open (and for the rest of the app), then set the field
      // straight away so the user sees their choice.
      await onMetaChange?.();
      set("kind", data.name);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add kind");
    }
  };

  const save = async () => {
    if (!form.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        kind: form.kind,
        interval: form.interval,
        first_visit_date: form.first_visit_date || null,
        contact_name: form.contact_name || null,
        phone: form.phone || null,
        email: form.email || null,
        website: form.website || null,
        cost_per_visit: form.cost_per_visit === "" ? null : Number(form.cost_per_visit),
        notes: form.notes || null,
        auto_log: !!form.auto_log,
      };
      if (isEdit) {
        await api.patch(`/site-services/providers/${provider.id}`, body);
        toast.success("Updated");
      } else {
        await api.post("/site-services/providers", body);
        toast.success("Provider added");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm(`Archive ${provider.name}? Their visit history stays.`))
      return;
    setBusy(true);
    try {
      await api.delete(`/site-services/providers/${provider.id}`);
      toast.success("Archived");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not archive");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
      data-testid="site-service-form-dialog"
    >
      <div
        className="nb-card w-[min(640px,96vw)] max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="label-eyebrow">
              {isEdit ? "Edit provider" : "New provider"}
            </div>
            <h3 className="font-display text-2xl">
              {isEdit ? provider.name : "Add Site Service Provider"}
            </h3>
          </div>
          <button
            data-testid="site-service-form-close"
            onClick={onClose}
            className="nb-btn bg-white p-2"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="label-eyebrow">Provider name *</span>
            <input
              data-testid="site-service-name"
              value={form.name || ""}
              onChange={(e) => set("name", e.target.value)}
              className="nb-input px-3 py-2"
              placeholder="e.g. Rentokil, Chubb Fire, Cleanaway"
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="label-eyebrow flex items-center justify-between">
                <span>Kind *</span>
                <button
                  type="button"
                  data-testid="site-service-kind-add"
                  onClick={addNewKind}
                  className="text-[#0A6DBE] hover:underline text-[10px] uppercase tracking-wider"
                >
                  + Add new
                </button>
              </span>
              <select
                data-testid="site-service-kind"
                value={form.kind || ""}
                onChange={(e) => set("kind", e.target.value)}
                className="nb-input px-3 py-2"
              >
                {(meta?.kinds || []).map((k) => (
                  <option key={k}>{k}</option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="label-eyebrow">Interval *</span>
              <select
                data-testid="site-service-interval"
                value={form.interval || ""}
                onChange={(e) => set("interval", e.target.value)}
                className="nb-input px-3 py-2"
              >
                {(meta?.intervals || []).map((iv) => (
                  <option key={iv}>{iv}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="label-eyebrow">First / anchor visit date</span>
              <input
                data-testid="site-service-first-visit"
                type="date"
                value={form.first_visit_date || ""}
                onChange={(e) => set("first_visit_date", e.target.value)}
                className="nb-input px-3 py-2"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="label-eyebrow">Cost per visit ($)</span>
              <input
                data-testid="site-service-cost"
                type="number"
                inputMode="decimal"
                min="0"
                value={form.cost_per_visit ?? ""}
                onChange={(e) => set("cost_per_visit", e.target.value)}
                className="nb-input px-3 py-2"
                placeholder="Optional"
              />
            </label>
          </div>

          <label
            className="flex items-start gap-2 nb-card-sm p-3 cursor-pointer"
            data-testid="site-service-auto-log-row"
          >
            <input
              type="checkbox"
              data-testid="site-service-auto-log"
              checked={!!form.auto_log}
              onChange={(e) => set("auto_log", e.target.checked)}
              className="w-4 h-4 mt-0.5"
            />
            <span className="text-sm">
              <b>Auto-log visits</b>
              <span className="block text-xs text-[#607087]">
                Each due visit is logged automatically as Completed at 6am (cost = cost per visit), back-filling any
                missed cycles. Mark a visit Incomplete if it didn't happen. Untick for services that need a manual log.
              </span>
            </span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="label-eyebrow">Contact person</span>
            <input
              data-testid="site-service-contact"
              value={form.contact_name || ""}
              onChange={(e) => set("contact_name", e.target.value)}
              className="nb-input px-3 py-2"
              placeholder="Who's the account manager?"
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="label-eyebrow">Phone</span>
              <input
                data-testid="site-service-phone"
                value={form.phone || ""}
                onChange={(e) => set("phone", e.target.value)}
                className="nb-input px-3 py-2"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="label-eyebrow">Email</span>
              <input
                data-testid="site-service-email"
                value={form.email || ""}
                onChange={(e) => set("email", e.target.value)}
                className="nb-input px-3 py-2"
                type="email"
              />
            </label>
          </div>

          <label className="flex flex-col gap-1">
            <span className="label-eyebrow">Website</span>
            <input
              data-testid="site-service-website"
              value={form.website || ""}
              onChange={(e) => set("website", e.target.value)}
              className="nb-input px-3 py-2"
              placeholder="https://…"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="label-eyebrow">Notes</span>
            <textarea
              data-testid="site-service-notes"
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
              className="nb-input px-3 py-2"
              rows={3}
              placeholder="Access instructions, quirks, escalation contacts…"
            />
          </label>
        </div>

        <div className="sticky bottom-0 bg-white p-4 border-t-2 border-black flex justify-between gap-2">
          {isEdit ? (
            <button
              data-testid="site-service-form-delete"
              disabled={busy}
              onClick={remove}
              className="nb-btn bg-[#FCE4E4] text-[#8A1F1F] border-[#8A1F1F] px-3 py-2 flex items-center gap-1.5"
            >
              <Trash2 size={14} strokeWidth={2.5} /> Archive
            </button>
          ) : (
            <span />
          )}
          <button
            data-testid="site-service-form-save"
            disabled={busy}
            onClick={save}
            className="nb-btn nb-btn-dark px-4 py-2 flex items-center gap-1.5"
          >
            <Save size={14} strokeWidth={2.5} /> {isEdit ? "Save changes" : "Add provider"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SiteServiceFormDialog;
